import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CalendarClock, Check, RotateCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { toneOf } from "@/lib/absences";

const initials = (n) => (n || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

// Žádosti o volno a hlášení nemoci ke schválení (176).
// variant="card" — Přehled; variant="banner" — nad šichtovnicí, ať to „křičí".
export default function AbsenceApprovals({ variant = "card", reloadKey = 0, onChanged, onShow }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [reject, setReject] = useState(null);
  const [reason, setReason] = useState("");
  const [all, setAll] = useState(false);

  const load = useCallback(() => {
    api.get("/absences/pending").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load, reloadKey]);

  const act = async (it, kind) => {
    setBusy(it.id);
    try {
      if (kind === "approve") await api.post(`/absences/${it.id}/approve`);
      else await api.post(`/absences/${it.id}/reject`, { reason: reason.trim() || null });
      toast.success(kind === "approve" ? (it.ack ? "Vzato na vědomí." : "Schváleno.") : "Žádost zamítnuta.");
      setReject(null); setReason("");
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (!items || items.length === 0) return null;
  const banner = variant === "banner";
  const shown = banner && !all ? items.slice(0, 3) : items;

  return (
    <div data-testid={banner ? "absence-banner" : "absence-approvals"}
      className={banner ? "rounded-[16px] border-2 border-brand/60 bg-brand/5 p-3.5 md:p-4" : "bg-card border border-brand/40 rounded-[16px] p-4 md:p-5"}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 className="text-[16px] md:text-[17px] font-bold tracking-tight flex items-center gap-2">
          <CalendarClock size={19} className="text-brand shrink-0" />
          {banner ? "Čeká na schválení" : "Nepřítomnost ke schválení"}
          <span className="rounded-full bg-brand text-white px-2.5 py-0.5 text-xs font-bold tabular-nums" data-testid="absence-pending-count">{items.length}</span>
        </h2>
      </div>
      <div className="divide-y divide-border/70">
        {shown.map((it) => (
          <div key={it.id} className="py-3 flex flex-col md:flex-row md:items-center gap-2.5 md:gap-4" data-testid={`absence-req-${it.id}`}>
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <span className="w-8 h-8 rounded-full bg-brand/15 text-brand grid place-items-center text-[11px] font-extrabold shrink-0">{initials(it.user_name)}</span>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold">{it.user_name}</span>
                  <span className={`inline-flex items-center h-6 px-2 rounded-full text-[12px] font-bold ${toneOf(it.code)}`}>{it.type_label}</span>
                  {it.makeup && <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[12px] font-bold bg-secondary text-foreground" title="Hodiny si napracuje jindy v měsíci — fond se nesníží" data-testid={`absence-makeup-${it.id}`}><RotateCcw size={12} /> napracuje</span>}
                  <span className="text-[13px] text-muted-foreground tabular-nums">{it.when}</span>
                </div>
                {(it.note || onShow) && (
                  <div className="text-[13px] text-muted-foreground mt-0.5">
                    {it.note && <>„{it.note}"</>}
                    {onShow && <>{it.note ? " · " : ""}<button type="button" onClick={() => onShow(it)} className="font-bold text-brand hover:underline">v šichtovnici</button></>}
                  </div>
                )}
              </div>
            </div>
            {it.ack ? (
              <Button onClick={() => act(it, "approve")} disabled={busy === it.id} variant="outline" data-testid={`absence-ack-${it.id}`}
                className="h-11 md:h-9 w-full md:w-auto font-bold shrink-0"><Check size={15} /> Beru na vědomí</Button>
            ) : (
              <div className="grid grid-cols-2 md:flex gap-2 shrink-0">
                <Button variant="outline" onClick={() => { setReason(""); setReject(it); }} disabled={busy === it.id} data-testid={`absence-reject-${it.id}`}
                  className="h-11 md:h-9 font-bold">Zamítnout</Button>
                <Button onClick={() => act(it, "approve")} disabled={busy === it.id} data-testid={`absence-approve-${it.id}`}
                  className="h-11 md:h-9 font-bold bg-success hover:bg-success/90 text-white"><Check size={15} /> Schválit</Button>
              </div>
            )}
          </div>
        ))}
      </div>
      {banner && items.length > 3 && (
        <button type="button" onClick={() => setAll((a) => !a)} className="text-[13px] font-bold text-brand mt-1" data-testid="absence-banner-more">
          {all ? "Sbalit" : `Zobrazit všechny (${items.length})`}
        </button>
      )}

      <Dialog open={!!reject} onOpenChange={(o) => { if (!o) setReject(null); }}>
        <DialogContent sheet className="md:max-w-md" data-testid="absence-reject-dialog">
          <DialogHeader>
            <DialogTitle>Zamítnout žádost?</DialogTitle>
            <DialogDescription>{reject ? `${reject.user_name} · ${reject.type_label} · ${reject.when}` : ""}</DialogDescription>
          </DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={300}
            placeholder="Důvod (nepovinné) — dostane ho v upozornění" data-testid="absence-reject-reason" />
          <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2">
            <Button variant="outline" onClick={() => setReject(null)} className="h-11 w-full md:w-auto">Zpět</Button>
            <Button variant="destructive" onClick={() => act(reject, "reject")} disabled={busy === reject?.id} className="h-11 w-full md:w-auto font-bold" data-testid="absence-reject-confirm">Zamítnout</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
